import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, CalendarDays } from "lucide-react";
import { api, fetchMe, googleLogin, INTEREST_LABELS } from "@/lib/api";

const budgetOptions = [
  { value: "under-50L", label: "Under ₹50 Lakhs" },
  { value: "50L-2Cr", label: "₹50 Lakhs — ₹2 Crore" },
  { value: "2Cr+", label: "₹2 Crore+" },
];

const timeSlots = ["10:00 AM", "12:00 PM", "3:00 PM", "5:30 PM"];

export const LeadCapture = ({ interest }) => {
  const [mode, setMode] = useState("enquiry");
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", interest: interest || "land",
    budget: "", message: "", date: "", time_slot: timeSlots[0],
  });

  useEffect(() => {
    fetchMe().then((u) => {
      if (u) {
        setUser(u);
        setForm((f) => ({ ...f, name: f.name || u.name, email: f.email || u.email }));
      }
    });
  }, []);

  useEffect(() => {
    if (interest) setForm((f) => ({ ...f, interest }));
  }, [interest]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "enquiry") {
        const res = await api.post("/leads", {
          name: form.name, email: form.email, phone: form.phone || null,
          interest: form.interest, budget: form.budget || null, message: form.message || null,
        });
        toast.success(
          res.data.status === "qualified"
            ? "You're pre-qualified. Our senior desk will reach out within hours."
            : "Interest received. Our team will be in touch shortly."
        );
      } else {
        if (!form.date) {
          toast.error("Please pick a preferred date.");
          setSubmitting(false);
          return;
        }
        await api.post("/consultations", {
          name: form.name, email: form.email, phone: form.phone || null,
          date: form.date, time_slot: form.time_slot,
          topic: INTEREST_LABELS[form.interest],
        });
        toast.success(`Consultation requested for ${form.date} at ${form.time_slot}. Confirmation follows by email.`);
      }
      setForm((f) => ({ ...f, message: "", date: "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="border-t border-white/5 bg-[#0F1116]" data-testid="lead-capture-section">
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="px-6 md:px-12 py-24 md:py-32 lg:pr-20 border-b lg:border-b-0 lg:border-r border-white/5">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#C8A97E] mb-6">Private Client Desk</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#F8F9FA] leading-[1.05] tracking-tight">
              Begin with a <span className="italic text-[#C8A97E]">conversation.</span>
            </h2>
            <p className="mt-8 text-sm md:text-base text-[#8A8D98] leading-relaxed max-w-md">
              Sign in with your Google account and you're instantly verified — no forms to repeat,
              no cold calls. Every enquiry is auto-qualified and routed to the senior advisor
              for your asset class.
            </p>
            <button
              onClick={() => googleLogin("/portal")}
              data-testid="google-login-button"
              className="btn-gold group mt-10 flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium"
            >
              <span>{user ? `Signed in as ${user.name.split(" ")[0]}` : "Continue with Google"}</span>
              <ArrowRight size={15} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <div className="mt-10 flex items-center gap-3 text-[#8A8D98]">
              <ShieldCheck size={16} className="text-[#C8A97E]" />
              <p className="font-mono-lux text-[10px] uppercase tracking-[0.2em]">
                Verified Google ID · Encrypted · Never shared
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="px-6 md:px-12 py-24 md:py-32 lg:pl-20"
        >
          <div className="flex gap-6 mb-12" role="tablist" data-testid="lead-mode-tabs">
            {[["enquiry", "Submit Interest"], ["consultation", "Book a Slot"]].map(([m, label]) => (
              <button
                key={m}
                role="tab"
                aria-selected={mode === m}
                data-testid={`mode-tab-${m}`}
                onClick={() => setMode(m)}
                className={`pb-2 text-[11px] font-mono-lux uppercase tracking-[0.25em] border-b transition-colors duration-300 ${
                  mode === m ? "border-[#C8A97E] text-[#F8F9FA]" : "border-transparent text-[#8A8D98] hover:text-[#F8F9FA]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-8" data-testid="lead-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className="lux-input" data-testid="lead-name-input" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className="lux-input" data-testid="lead-email-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input placeholder="Phone (optional, speeds up qualification)" value={form.phone} onChange={set("phone")} className="lux-input" data-testid="lead-phone-input" />
              <select value={form.interest} onChange={set("interest")} className="lux-input" data-testid="lead-interest-select">
                {Object.entries(INTEREST_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            {mode === "enquiry" ? (
              <>
                <select value={form.budget} onChange={set("budget")} className="lux-input" data-testid="lead-budget-select">
                  <option value="">Indicative budget (optional)</option>
                  {budgetOptions.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
                <textarea placeholder="Tell us what you're looking for (optional)" value={form.message} onChange={set("message")} rows={3} className="lux-input resize-none" data-testid="lead-message-input" />
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input required type="date" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} className="lux-input [color-scheme:dark]" data-testid="consult-date-input" />
                <select value={form.time_slot} onChange={set("time_slot")} className="lux-input" data-testid="consult-slot-select">
                  {timeSlots.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-button"
              className="btn-gold group flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium disabled:opacity-50"
            >
              <span>{submitting ? "Sending…" : mode === "enquiry" ? "Submit Interest" : "Request Consultation"}</span>
              {mode === "consultation" ? (
                <CalendarDays size={15} className="relative z-10" />
              ) : (
                <ArrowRight size={15} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
